import { Button, Container, Label, TextInput } from '@playcanvas/pcui';

import { Events } from '../../events';
import { login } from '../backend';

// "Sign in to Olloport" — email + password sign-in inside the editor. On
// success it stores a session token (see ollo-backend.login) that auths every
// backend call as a Bearer, working on localhost AND the deployed editor.
// Reuses the shared `settings-dialog` styling (see image-settings-dialog).
class LoginDialog extends Container {
    show: () => Promise<boolean>;
    hide: () => void;
    destroy: () => void;

    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'login-dialog',
            class: 'settings-dialog',
            hidden: true,
            tabIndex: -1
        };

        super(args);

        const dialog = new Container({ id: 'dialog' });

        // header
        const headerText = new Label({ id: 'text', text: 'SIGN IN TO OLLOPORT' });
        const header = new Container({ id: 'header' });
        header.append(headerText);

        // email
        const emailLabel = new Label({ class: 'label', text: 'Email' });
        const emailInput = new TextInput({ class: 'select', value: '' });
        const emailRow = new Container({ class: 'row' });
        emailRow.append(emailLabel);
        emailRow.append(emailInput);

        // password
        const passwordLabel = new Label({ class: 'label', text: 'Password' });
        const passwordInput = new TextInput({ class: 'select', value: '' });
        const passwordRow = new Container({ class: 'row' });
        passwordRow.append(passwordLabel);
        passwordRow.append(passwordInput);

        // raw <input> handles for password masking + focus (pcui has no direct API)
        const emailEl = emailInput.dom.querySelector('input') as HTMLInputElement | null;
        const passwordEl = passwordInput.dom.querySelector('input') as HTMLInputElement | null;
        if (emailEl) emailEl.type = 'email';
        if (passwordEl) passwordEl.type = 'password';

        // error line (hidden until a sign-in fails)
        const errorLabel = new Label({ class: 'label', text: '', hidden: true });
        errorLabel.dom.style.color = '#ff6b6b';
        errorLabel.dom.style.whiteSpace = 'normal';

        // content
        const content = new Container({ id: 'content' });
        content.append(emailRow);
        content.append(passwordRow);
        content.append(errorLabel);

        // footer
        const footer = new Container({ id: 'footer' });
        const cancelButton = new Button({ class: 'button', text: 'Cancel' });
        const okButton = new Button({ class: 'button', text: 'Sign in' });
        footer.append(cancelButton);
        footer.append(okButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);

        this.append(dialog);

        let onCancel: () => void;
        let onOK: () => void;

        cancelButton.on('click', () => onCancel());
        okButton.on('click', () => onOK());

        const keydown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onCancel();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                onOK();
            }
        };

        this.show = () => {
            errorLabel.hidden = true;
            errorLabel.text = '';
            passwordInput.value = '';
            okButton.enabled = true;
            okButton.text = 'Sign in';

            this.hidden = false;
            document.addEventListener('keydown', keydown);
            (emailEl ?? this.dom).focus();

            return new Promise<boolean>((resolve) => {
                let settled = false;
                const finish = (result: boolean) => {
                    if (settled) return;
                    settled = true;
                    document.removeEventListener('keydown', keydown);
                    this.hide();
                    resolve(result);
                };

                onCancel = () => finish(false);

                onOK = async () => {
                    const email = (emailInput.value || '').trim();
                    const password = passwordInput.value || '';
                    if (!email || !password) {
                        errorLabel.text = 'Enter your email and password.';
                        errorLabel.hidden = false;
                        return;
                    }

                    okButton.enabled = false;
                    okButton.text = 'Signing in…';
                    const res = await login(email, password);
                    okButton.enabled = true;
                    okButton.text = 'Sign in';

                    if (res.ok) {
                        finish(true);
                    } else {
                        errorLabel.text = res.error === 'invalid-credentials'
                            ? 'Wrong email or password.'
                            : (res.error || 'Sign-in failed. Please try again.');
                        errorLabel.hidden = false;
                    }
                };
            });
        };

        this.hide = () => {
            this.hidden = true;
        };

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { LoginDialog };
